// PostMessageIdLayout.jsx
import React from 'react';
import { Outlet, useLoaderData } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchMessageDetail from '../action/fetchMessageDetail.js';
import fetchUser from '../action/fetchUser.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../resource/defaults.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import { prepareVideoPageMeta } from '../resource/prepareVideoPageMeta.js';
import { getVideoFallback } from '../resource/getMediaAsset.js';
import useSSRDataMerge from '../hook/useSSRDataMerge.js';
import getMessageData from '../selector/getMessageData.js';
import shouldSkipSSRFetch from '../resource/shouldSkipSSRFetch.js';
import getMessageDetailLayout from '../selector/getMessageDetailLayout.js';
import getPostMessageLcpPreloadMetaTags from '../resource/getPostMessageLcpPreloadMetaTags.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('PostMessageIdLayout loader()');
  const errorLog = legacySSRError.extend('PostMessageIdLayout loader()');
  log('start', { context, params, request });

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });

    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    const skipSSRFetch = shouldSkipSSRFetch({
      requestUrl: new URL(request.url),
      state: store.getState(),
    });
    if (skipSSRFetch) {
      log('skip SSR fetch due to buster query');
      return { data: null, params };
    }

    // Fetch message detail
    await store.dispatch(
      fetchMessageDetail({
        id: params.messageId,
        httpProxyHeaders,
      })
    );

    const state = store.getState();

    // Fetch user detail for SEO meta
    const senderId = getMessageData(state, params.messageId, 'senderId');
    if (senderId) {
      await store.dispatch(
        fetchUser({
          id: senderId,
          httpProxyHeaders,
        })
      );
    }

    const finalState = store.getState();
    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );
    const canonicalPath = `/post/${params.messageId}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareVideoPageMeta({
      state: finalState,
      messageId: params.messageId,
      hostname: request?.headers?.get('host') || 'swag.live',
      canonical,
      alternates,
      includeJsonLd: true,
      forceNoindex: false,
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    const assetIds = getMessageData(finalState, params.messageId, 'assetIds');
    const videoFallback = getVideoFallback({
      messageId: params.messageId,
      assetIds,
    });

    const layoutType =
      getMessageDetailLayout(finalState, params.messageId) || 'post';

    const lcpPreloadMetaTags = getPostMessageLcpPreloadMetaTags({
      state: finalState,
      messageId: params.messageId,
      layoutType,
    });

    const metaTags = [...(meta || EMPTY_ARRAY), ...lcpPreloadMetaTags];

    return {
      data: {
        store: { messages: storeData.messages, users: storeData.users },
        layoutType,
      },
      params,
      meta: metaTags,
      videoFallback,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const PostMessageIdLayout = () => {
  const {
    data,
    error,
    meta = EMPTY_ARRAY,
    videoFallback,
  } = useLoaderData() || EMPTY_OBJECT;

  useSSRDataMerge({
    componentName: 'PostMessageIdLayout',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      {videoFallback && (
        <video
          poster={videoFallback.posterUrl}
          preload="none"
          muted
          aria-hidden="true"
          style={seoVideoStyle}
        >
          <source src={videoFallback.contentUrl} type="video/mp4" />
        </video>
      )}
      <Outlet context={{ layoutType: data?.layoutType }} />
    </>
  );
};

// 1x1 transparent <video> for SEO crawlers (UniversalComponent won't render in SSR).
// ref: https://developers.google.com/search/docs/appearance/video
const seoVideoStyle = {
  width: 1,
  height: 1,
  opacity: 0,
  position: 'absolute',
  pointerEvents: 'none',
};

export default PostMessageIdLayout;
